import { useEffect, useState, useMemo, FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import {
  GetAddressResponseAddress,
  getEnecoBeXapiSiteApiV1AddressesSearchOnZipcode,
  getEnecoBeXapiSiteApiV1AddressesSearchOnCityname,
  getEnecoBeXapiSiteApiV1AddressesSearchOnZipcodeAndStreetname,
  LanguageCodeDto,
} from '@eneco/dc-be/client';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { InputCombobox, InputText, NotificationBox, Stack } from '@eneco/sparky';
import { useFormContext, get } from 'react-hook-form';
import { useDebounce } from 'react-use';
import * as yup from 'yup';

const MIN_CHARACTERS_BEFORE_FETCH = 3;
export const createAddressSchema = (zipCodeError: string, cityError: string, streetNameError: string) =>
  yup.object({
    zipCode: yup.string().typeError(zipCodeError).required(zipCodeError),
    city: yup.string().required(cityError),
    streetName: yup.string().required(streetNameError),
  });

type YupPropsType = {
  name: string;
  onChange?: (event: Event) => void;
  onBlur?: (event: Event) => void;
  [key: string]: unknown;
};

type customFormField = {
  label?: string;
  defaultValue: string;
  hint?: string;
  yupProps: YupPropsType;
};

type ErrorProps = {
  zipCodeCityError?: string;
  streetNameNotFoundWarningTitle?: string;
  streetNameNotFoundWarningContent?: string;
};

type AddressAutocompleteProps = {
  shouldValidateAddressInApi: boolean;
  isDisabled?: boolean;
  onZipCodeCityChange: (value: { zipCode: string | undefined; city: string | undefined }) => void;
  onStreetNameChange?: (streetName: string) => void;
  zipCodeFormField: customFormField;
  cityFormField: customFormField;
  streetNameFormField?: customFormField;
  isApiAddressValidYupProps?: YupPropsType;
} & ErrorProps;

const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  cityFormField,
  streetNameFormField,
  zipCodeFormField,
  zipCodeCityError = '',
  streetNameNotFoundWarningContent = '',
  streetNameNotFoundWarningTitle = '',
  shouldValidateAddressInApi,
  isDisabled,
  onStreetNameChange,
  onZipCodeCityChange,
  isApiAddressValidYupProps,
}) => {
  const {
    formState: { errors, touchedFields },
    setError,
    watch,
    clearErrors,
    setValue,
  } = useFormContext();

  const zipCodePath = zipCodeFormField.yupProps.name;
  const cityPath = cityFormField.yupProps.name;
  const streetNamePath = streetNameFormField?.yupProps?.name ?? 'streetName';

  const zipCodeWatch = watch(zipCodePath) || '';
  const cityWatch = watch(cityPath) || '';
  const streetNameWatch = watch(streetNamePath) || '';

  const isZipCodeTouched = get(touchedFields, zipCodePath);

  const {
    defaultValue: zipCodeDefaultValue,
    label: zipCodeLabel,
    hint: zipCodeHint,
    yupProps: zipCodeYupProps,
  } = zipCodeFormField;
  const { defaultValue: cityDefaultValue, label: cityLabel, hint: cityHint, yupProps: cityYupProps } = cityFormField;
  const {
    defaultValue: streetNameDefaultValue,
    label: streetNameLabel,
    hint: streetNameHint,
    yupProps: streetNameYupProps,
  } = streetNameFormField ?? {};

  const zipCodeCityDefaultValue =
    zipCodeDefaultValue && cityDefaultValue ? `${zipCodeDefaultValue} - ${cityDefaultValue.toLocaleUpperCase()}` : '';

  const [zipCodeOptions, setZipCodeOptions] = useState<GetAddressResponseAddress[] | undefined>(undefined);
  const [streetNameOptions, setStreetNameOptions] = useState<GetAddressResponseAddress[]>([]);

  const [zipCodeSearchRequest, setZipCodeSearchRequest] = useState<string | undefined>();
  const [debouncedZipCodeSearchRequest, setDebouncedZipCodeSearchRequest] = useState<string | undefined>();

  const [streetnameSearchRequest, setStreetnameSearchRequest] = useState<string | undefined>();
  const [debouncedStreetnameSearchRequest, setDebouncedStreetnameSearchRequest] = useState<string | undefined>();

  const [isInWarningStreetName, setIsInWarningStreetName] = useState<boolean>(false);
  const [showIsInWarningStreetName, setShowIsInWarningStreetName] = useState<boolean>(false);

  const isNumeric = (value: string) => /^\d+$/.test(value);
  const isZipCodeSearch = debouncedZipCodeSearchRequest && isNumeric(debouncedZipCodeSearchRequest);
  const isCitySearch = debouncedZipCodeSearchRequest && !isNumeric(debouncedZipCodeSearchRequest);

  const shouldFetchZipCode = Boolean(
    isZipCodeSearch && debouncedZipCodeSearchRequest!.length >= MIN_CHARACTERS_BEFORE_FETCH,
  );
  const shouldFetchCity = Boolean(isCitySearch && debouncedZipCodeSearchRequest!.length >= MIN_CHARACTERS_BEFORE_FETCH);
  const shouldFetchStreetname = Boolean(
    debouncedStreetnameSearchRequest && debouncedStreetnameSearchRequest.length >= MIN_CHARACTERS_BEFORE_FETCH,
  );

  const { name: zipCodeFormname, onBlur: _, onChange: __, ...restZipCodeYupProps } = zipCodeYupProps;
  const { name: cityFormname, onBlur: ___, onChange: ____, ...restCityYupProps } = cityYupProps;
  const {
    name: streetNameFormname,
    onBlur: _____,
    onChange: ______,
    ...restStreetNameYupProps
  } = streetNameYupProps ?? {};

  const {
    data: zipCodeCitySearchData,
    isLoading: isLoadingZipCodeCitySearch,
    error: zipCodeCitySearchError,
  } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1AddressesSearchOnZipcode,
    {
      query: {
        zipcodepart: debouncedZipCodeSearchRequest ?? '',
        language: 'NL' as LanguageCodeDto,
      },
    },
    ['/addresses/searchOnZipcode', debouncedZipCodeSearchRequest ?? ''],
    shouldFetchZipCode,
  );

  const uniqueAddresses = useMemo(() => {
    const addresses = unwrapData(zipCodeCitySearchData)?.addresses;
    if (!addresses) return [];
    const seen = new Set<string>();
    return addresses.filter(item => {
      const key = `${item.zipcode}-${item.city}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [zipCodeCitySearchData]);

  const {
    data: citySearchData,
    isLoading: isLoadingCitySearch,
    error: citySearchError,
  } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1AddressesSearchOnCityname,
    {
      query: {
        cityName: debouncedZipCodeSearchRequest ?? '',
        language: 'NL' as LanguageCodeDto,
      },
    },
    ['/addresses/searchOnCityname', debouncedZipCodeSearchRequest ?? ''],
    shouldFetchCity,
  );

  const { data: streetNameSearchData, error: streetNameSearchError } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1AddressesSearchOnZipcodeAndStreetname,
    {
      query: {
        zipCode: debouncedZipCodeSearchRequest ?? '',
        streetNamePart: debouncedStreetnameSearchRequest ?? '',
        language: 'NL' as LanguageCodeDto,
      },
    },
    [
      '/api/v1/addresses/searchOnZipcodeAndStreetname',
      debouncedStreetnameSearchRequest ?? '',
      debouncedZipCodeSearchRequest ?? '',
    ],
    shouldFetchStreetname,
  );

  useEffect(() => {
    if (
      zipCodeCitySearchError ||
      zipCodeCitySearchData?.error?.errors ||
      citySearchError ||
      citySearchData?.error?.errors
    )
      setZipCodeOptions([]);

    if (streetNameSearchError || streetNameSearchData?.error?.errors) setStreetNameOptions([]);
  }, [
    zipCodeCitySearchError,
    streetNameSearchError,
    citySearchError,
    zipCodeCitySearchData?.error?.errors,
    streetNameSearchData?.error?.errors,
    citySearchData?.error?.errors,
  ]);

  useDebounce(
    () => {
      setDebouncedZipCodeSearchRequest(zipCodeSearchRequest);
    },
    300,
    [zipCodeSearchRequest],
  );
  useDebounce(
    () => {
      setDebouncedStreetnameSearchRequest(streetnameSearchRequest);
    },
    300,
    [streetnameSearchRequest],
  );

  useEffect(() => {
    const validateZipCodeAndCity = () => {
      if (!shouldValidateAddressInApi) return;
      const isValid = zipCodeOptions?.some(
        option =>
          option.zipcode === zipCodeWatch && option.city?.toLocaleLowerCase() === cityWatch?.toLocaleLowerCase(),
      );
      isApiAddressValidYupProps && setValue(isApiAddressValidYupProps?.name, isValid, { shouldValidate: true });
      const currentError = get(errors, zipCodePath)?.message;
      if (isValid) {
        clearErrors(zipCodePath);
      } else if (!isValid && currentError !== zipCodeCityError && isZipCodeTouched) {
        setError(zipCodePath, { type: 'manual', message: zipCodeCityError });
      }
    };

    if (!isLoadingZipCodeCitySearch && !isLoadingCitySearch && zipCodeOptions !== undefined) validateZipCodeAndCity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cityWatch,
    clearErrors,
    isLoadingZipCodeCitySearch,
    isLoadingCitySearch,
    setError,
    shouldValidateAddressInApi,
    zipCodeCityError,
    zipCodePath,
    zipCodeOptions,
    zipCodeWatch,
    errors,
    setValue,
  ]);

  useEffect(() => {
    const isMatch = streetNameOptions.some(
      option => option.street?.toLocaleLowerCase() === streetNameWatch?.toLocaleLowerCase(),
    );
    setIsInWarningStreetName(!isMatch);
  }, [streetNameOptions, streetNameWatch]);

  const splitZipCodeAndCity = (value: string): { zipCode: string; city: string } => {
    const zipCode = value.match(/\d{4}/g);
    const city = value.replace(/\d{4}/, '').replace(' - ', '');

    return { zipCode: zipCode?.[0] ?? '', city };
  };

  const handleZipCodeAndCityChange = (value: string) => {
    if (value.length < MIN_CHARACTERS_BEFORE_FETCH && zipCodeOptions && zipCodeOptions.length > 0) {
      setZipCodeOptions([]);
      setStreetNameOptions([]);
    }
    const zipCodeAndCity = splitZipCodeAndCity(value);
    if (zipCodeAndCity.zipCode && zipCodeAndCity.city) setZipCodeSearchRequest(zipCodeAndCity.zipCode);
    else setZipCodeSearchRequest(value);

    if (zipCodeAndCity.zipCode !== zipCodeWatch || zipCodeAndCity.city !== cityWatch) {
      if (shouldValidateAddressInApi && isApiAddressValidYupProps) {
        setValue(isApiAddressValidYupProps.name, false, { shouldValidate: true });
      }
      onZipCodeCityChange(zipCodeAndCity);
    }
  };

  const handleStreetNameChange = (value: string) => {
    if (value.length < MIN_CHARACTERS_BEFORE_FETCH && streetNameOptions && streetNameOptions.length > 0)
      setStreetNameOptions([]);
    else if (zipCodeWatch?.length > 0 && cityWatch?.length > 0) {
      setStreetnameSearchRequest(value);
    }
    if (value !== streetNameWatch) {
      onStreetNameChange && onStreetNameChange(value);
    }
  };

  useEffect(() => {
    if (uniqueAddresses && uniqueAddresses.length > 0) setZipCodeOptions(uniqueAddresses);
    else if (citySearchData?.data?.data?.addresses && citySearchData?.data?.data?.addresses.length > 0)
      setZipCodeOptions(citySearchData?.data?.data?.addresses);

    if (
      streetNameSearchData?.data?.data?.addresses &&
      streetNameSearchData?.data?.data?.addresses.length > 0 &&
      !get(errors, zipCodePath)?.message &&
      zipCodeWatch?.length > 0 &&
      cityWatch?.length > 0
    ) {
      const uniqueStreetNames = streetNameSearchData?.data?.data?.addresses?.filter(
        (value, index, self) => index === self.findIndex(t => t.street?.toLowerCase() === value.street?.toLowerCase()),
      );
      setStreetNameOptions(uniqueStreetNames);
    }
  }, [
    uniqueAddresses,
    citySearchData,
    streetNameSearchData,
    zipCodeWatch?.length,
    cityWatch?.length,
    errors,
    zipCodePath,
  ]);

  return shouldValidateAddressInApi ? (
    <>
      <InputCombobox
        name={zipCodeFormname as string}
        label={[zipCodeLabel, cityLabel].filter(Boolean).join(' - ')}
        hint={<RichText html={zipCodeHint} />}
        error={
          get(errors, zipCodePath)?.message?.toString() ||
          get(errors, isApiAddressValidYupProps?.name)?.message?.toString() ||
          ''
        }
        defaultValue={zipCodeCityDefaultValue}
        isDisabled={isDisabled}
        onChange={handleZipCodeAndCityChange}
        options={zipCodeOptions?.map(address => `${address.zipcode} - ${address.city}`)}
        onBlur={() => {
          streetNameWatch && streetNameWatch?.length > 0 && setShowIsInWarningStreetName(true);
        }}
        {...restZipCodeYupProps}
      />
      {streetNameFormField && onStreetNameChange && (
        <Stack gap={4}>
          <InputCombobox
            name={streetNameFormname as string}
            label={streetNameLabel ?? ''}
            hint={<RichText html={streetNameHint} />}
            error={get(errors, streetNamePath)?.message?.toString()}
            defaultValue={streetNameDefaultValue ?? ''}
            isDisabled={isDisabled}
            onChange={handleStreetNameChange}
            options={streetNameOptions.map(address => address.street ?? '')}
            onBlur={() => {
              setShowIsInWarningStreetName(true);
            }}
            {...restStreetNameYupProps}
          />
          {isInWarningStreetName && showIsInWarningStreetName && (
            <NotificationBox
              isAlert={false}
              title={streetNameNotFoundWarningTitle}
              text={<RichText html={streetNameNotFoundWarningContent} />}
              variant="warning"
            />
          )}
        </Stack>
      )}
    </>
  ) : (
    <>
      <InputText
        name={zipCodeFormname as string}
        label={zipCodeLabel ?? ''}
        hint={<RichText html={zipCodeHint} />}
        autoComplete="postal-code"
        error={get(errors, zipCodePath)?.message?.toString()}
        defaultValue={zipCodeDefaultValue}
        onChange={event => {
          const value = event.target.value;
          onZipCodeCityChange({ zipCode: value === '' ? undefined : value, city: cityWatch });
        }}
        isDisabled={isDisabled}
        {...restZipCodeYupProps}
      />
      <InputText
        name={cityFormname as string}
        label={cityLabel ?? ''}
        hint={<RichText html={cityHint} />}
        error={get(errors, cityPath)?.message?.toString()}
        onChange={event => {
          const value = event.target.value;
          onZipCodeCityChange({ city: value === '' ? undefined : value, zipCode: zipCodeWatch });
        }}
        defaultValue={cityDefaultValue}
        isDisabled={isDisabled}
        {...restCityYupProps}
      />
      {streetNameFormField && onStreetNameChange && (
        <InputText
          name={streetNameFormname as string}
          label={streetNameLabel ?? ''}
          hint={<RichText html={streetNameHint} />}
          error={get(errors, streetNamePath)?.message?.toString()}
          defaultValue={streetNameDefaultValue ?? ''}
          isDisabled={isDisabled}
          onChange={event => {
            const value = event.target.value;
            onStreetNameChange(value);
          }}
          {...restStreetNameYupProps}
        />
      )}
    </>
  );
};

export default AddressAutocomplete;
